<template>
  <div class="main">
    <div v-show="loading">
      <br />
      <Loading
        type="line"
        :center="true"
      />
      <div>Aguarde, estamos gerando o faturamento...</div>
    </div>
    <div v-show="!loading">
      <div v-if="!success">
        <SelectOptionPeriodRent
          v-show="isStepOne"
          :rentId="rentId"
          :showPeriodAll="true"
        />
        <DisplayTotalization
          title="TOTAL GERAL"
          :value="totalSelected"
          _style="color: #1b3a55;font-size:19px"
        />
        <Molded v-show="isStepOne">
          <ItemsRentForInvoice
            :rentId="rentId"
            :periodRentId="periodRentId"
          />
        </Molded>
        <div class="div-alert">
          <Alert
            type="warning"
            v-if="hasDifference && isStepTwo"
          >
            <span>
              Restante
              {{ difference | currency }}
            </span>
          </Alert>
        </div>
        <Molded v-if="isStepTwo">
          <PaymentCreate
            module="Rental"
            :totalValue="totalSelected"
          />
          <br />
          <PaymentsList />
        </Molded>
        <InvoiceOrTaxDocument
          v-show="isStepThree"
          v-model="invoiceOrTaxDocument"
        />
        <div v-show="isStepFour">
          <GenerateInvoice
            v-if="isInvoice"
            v-model="generateInvoice"
          />
          <NewGenerateNfse
            v-if="isNfse"
            :customerId="customerId"
            v-model="generateNfse"
          />
        </div>
        <br />
        <div class="text-center">
          <Button
            _key="btnClickedBack"
            v-if="showBackButton"
            type="info"
            title="Voltar"
            classIcon="title fa-solid fa-circle-arrow-left"
            size="small"
            :clicked="back"
          />
          <Button
            v-if="showNextButton"
            _key="btnClickedNext"
            type="info"
            title="Avançar"
            classIcon="title fas fa-arrow-circle-right"
            :disabled="disabled"
            size="medium"
            :clicked="next"
          />
          <Button
            v-if="showGenerateButton"
            _key="btnGenerateBilling"
            type="success"
            title="Gerar Faturamento"
            size="large"
            :clicked="generateBilling"
          />
        </div>
      </div>
      <div v-else>
        <SuccessBilling
          :typeDocument="typeDocument"
          :document="document"
          :rentId="rentId"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Alert from "@nixweb/nixloc-ui/src/component/layout/Alert";
import Button from "@nixweb/nixloc-ui/src/component/forms/Button";
import DisplayTotalization from "@nixweb/nixloc-ui/src/component/rental/DisplayTotalization";
import Loading from "@nixweb/nixloc-ui/src/component/shared/Loading.vue";
import Molded from "@nixweb/nixloc-ui/src/component/layout/Molded";

import GenerateInvoice from "./GenerateInvoice.vue";
import InvoiceOrTaxDocument from "./InvoiceOrTaxDocument.vue";
import ItemsRentForInvoice from "./ItemsRentForInvoice.vue";
import NewGenerateNfse from "./NewGenerateNfse.vue";
import PaymentCreate from "../../shared/payment-items/PaymentCreate.vue";
import PaymentsList from "../../shared/payment-items/PaymentsList.vue";
import SelectOptionPeriodRent from "../../rental/period-rental/SelectOptionPeriodRent.vue";
import SuccessBilling from "./SuccessBilling.vue";

import { mapGetters, mapMutations, mapActions, mapState } from "vuex";

const DOCUMENT_TYPE_INVOICE = 1;
const DOCUMENT_TYPE_NFSE = 2;

export default {
  name: "GenerateBilling",
  components: {
    ItemsRentForInvoice,
    Molded,
    Button,
    Alert,
    Loading,
    SelectOptionPeriodRent,
    DisplayTotalization,
    PaymentCreate,
    PaymentsList,
    InvoiceOrTaxDocument,
    GenerateInvoice,
    SuccessBilling,
    NewGenerateNfse,
  },
  data() {
    return {
      document: {},
      rentId: this.$route.params.rentId,
      customerId: this.$route.params.customerId,
      periodRentId: "",
      selected: [],
      invoiceOrTaxDocument: 0,
      generateInvoice: {
        otherCustomer: false,
        customer: {},
        isAddProductRent: false,
        isAddPeriodRent: false,
        isAddPayment: false,
        description: "",
        observation: "",
      },
      generateNfse: {
        otherCustomer: false,
        customer: {},
        emails: [],
        taxation: {},
        nacional: true,
        nfsePrefeitura: { valorNota: 0 },
        nfseNacional: { valorNota: 0 },
      },
      step: 1,
      urlGenerate: "/api/v1/billing/generate-billing",
      urlGetAllWithoutLinkedFinance:
        "/api/v1/rental/payment-rent/get-all-without-linked-finance",
      success: false,
      loading: false,
    };
  },
  mounted() {
    this.getAllWithoutLinkedFinance();
  },
  beforeDestroy() {
    this.resetPayment();
  },
  computed: {
    ...mapGetters("generic", ["event"]),
    ...mapGetters("paymentShared", ["sumPayment"]),
    ...mapState("paymentShared", ["paymentsRent"]),
    isStepOne() {
      return this.step === 1;
    },
    isStepTwo() {
      return this.step === 2;
    },
    isStepThree() {
      return this.step === 3;
    },
    isStepFour() {
      return this.step === 4;
    },
    isInvoice() {
      return this.invoiceOrTaxDocument === DOCUMENT_TYPE_INVOICE;
    },
    isNfse() {
      return this.invoiceOrTaxDocument === DOCUMENT_TYPE_NFSE;
    },
    hasDifference() {
      return this.difference !== 0;
    },
    showBackButton() {
      return this.step > 1;
    },
    showNextButton() {
      return this.step !== 4;
    },
    showGenerateButton() {
      return this.step === 4;
    },
    typeDocument() {
      return this.isInvoice ? "fatura" : "nota";
    },
    difference() {
      const totalSelected = Number(this.totalSelected || 0);
      const sumPayment = Number(this.sumPayment || 0);
      return Number((totalSelected - sumPayment).toFixed(2));
    },
    totalSelected() {
      return this.selected.reduce((accumulator, item) => {
        return accumulator + (item.totalUnitValue + item.totalUnitInsurance);
      }, 0);
    },
    disabled() {
      if (this.isStepOne) {
        return this.selected.length === 0;
      }

      if (this.isStepTwo) {
        return this.difference !== 0;
      }

      if (this.isStepThree) {
        return (
          this.invoiceOrTaxDocument !== DOCUMENT_TYPE_INVOICE &&
          this.invoiceOrTaxDocument !== DOCUMENT_TYPE_NFSE
        );
      }

      return true;
    },
    idsItemsRent() {
      return this.selected.map((item) => item.id);
    },
  },
  methods: {
    ...mapMutations("generic", ["removeLoading"]),
    ...mapMutations("paymentShared", ["addPayment", "resetPayment"]),
    ...mapActions("generic", ["getApi", "postApi"]),

    next() {
      this.step++;
      this.removeLoading(["btnClickedNext"]);
    },

    back() {
      this.step--;
      this.removeLoading(["btnClickedBack"]);
    },

    generateBilling() {
      this.loading = true;

      const params = {
        url: this.urlGenerate,
        obj: this.isInvoice ? this.createObjInvoice() : this.createObjNfse(),
      };

      this.postApi(params).then((response) => {
        if (response.success) {
          this.success = true;
          this.document = response.content;
        }

        this.loading = false;
        this.removeLoading(["btnGenerateBilling"]);
      });
    },

    mapPaymentRent(item) {
      return {
        id: item.id,
        typePayment: { id: item.typePaymentId, content: item.typePaymentName },
        dueDate: item.dueDate,
        value: item.value,
      };
    },

    getAllWithoutLinkedFinance() {
      const params = {
        url: this.urlGetAllWithoutLinkedFinance,
        obj: { any: this.rentId },
      };

      this.getApi(params).then((response) => {
        response.content.forEach((item) => {
          this.addPayment(this.mapPaymentRent(item));
        });
      });
    },

    createObjInvoice() {
      return {
        invoiceOrTaxDocument: this.invoiceOrTaxDocument,
        rentId: this.rentId,
        otherCustomer: this.generateInvoice.otherCustomer,
        customerId: this.generateInvoice.customer?.id || "",
        isAddProductRent: this.generateInvoice.isAddProductRent,
        isAddPeriodRent: this.generateInvoice.isAddPeriodRent,
        isAddPayment: this.generateInvoice.isAddPayment,
        description: this.generateInvoice.description,
        observation: this.generateInvoice.observation,
        paymentsRent: this.paymentsRent,
        idsItemsRent: this.idsItemsRent,
        taxation: this.generateNfse.taxation,
        value: this.totalSelected,
      };
    },

    createObjNfse() {
      this.generateNfse.nfsePrefeitura.valorNota = this.totalSelected;
      this.generateNfse.nfseNacional.valorNota = this.totalSelected;

      return {
        invoiceOrTaxDocument: this.invoiceOrTaxDocument,
        rentId: this.rentId,
        otherCustomer: this.generateNfse.otherCustomer,
        customerId: this.generateNfse.customer?.id || "",
        paymentsRent: this.paymentsRent,
        idsItemsRent: this.idsItemsRent,
        emails: this.generateNfse.emails,
        nacional: this.generateNfse.nacional,
        nfsePrefeitura: this.generateNfse.nfsePrefeitura,
        nfseNacional: this.generateNfse.nfseNacional,
        value: this.totalSelected,
      };
    },
  },
  watch: {
    event: {
      handler(event) {
        if (event.name === "selectedPeriodRent") {
          if (event.data.showAll) this.periodRentId = "";
          if (!event.data.showAll) this.periodRentId = event.data.period.id;
        }

        if (event.name === "itemsSelectedForInvoice") {
          this.selected = event.data;
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.main {
  margin-top: 20px;
}

.div-alert {
  margin-bottom: 20px;
}
</style>